import React from 'react';
import PageLayout from '../components/PageLayout';
import SEOHead from '../components/SEOHead';

const designServices = [
  {
    icon: '/pictures/focus_on_user_experience_and_design_7b8b7d13c5.svg',
    title: 'User Research & Discovery',
    desc: 'Deep user interviews, surveys, and behavioral analysis to understand your users\' real needs, pain points, and mental models before a single pixel is designed.',
  },
  {
    icon: '/pictures/delivering_customized_solutions_081a9bb187.svg',
    title: 'Information Architecture',
    desc: 'Organize and structure your content and features for maximum clarity. Sitemaps, user flows, and navigation systems that feel intuitive to every user.',
  },
  {
    icon: '/pictures/software_f1fba72847.svg',
    title: 'Wireframing & Prototyping',
    desc: 'Low and high-fidelity wireframes and interactive prototypes that validate design decisions early. Test and iterate before development begins to save time and cost.',
  },
  {
    icon: '/pictures/innovation_561a49ed62.svg',
    title: 'Visual UI Design',
    desc: 'Pixel-perfect visual designs that reflect your brand identity. Typography, color systems, iconography, and component libraries built for consistency and scale.',
  },
  {
    icon: '/pictures/improved_quality_841c5f0827.svg',
    title: 'Usability Testing',
    desc: 'Validate designs with real users through moderated and unmoderated testing sessions. Data-driven iteration to continuously improve conversion and satisfaction.',
  },
  {
    icon: '/pictures/global_scale_94d2c82201.svg',
    title: 'Design System Creation',
    desc: 'Build a reusable, scalable design system with documented components, patterns, and guidelines. Ensure consistency across your entire product ecosystem.',
  },
];

const process = [
  { step: 'Discover', desc: 'Understand users, business goals, and competitive landscape.' },
  { step: 'Define', desc: 'Synthesize research into personas, journeys, and design principles.' },
  { step: 'Design', desc: 'Create wireframes, prototypes, and high-fidelity visual designs.' },
  { step: 'Test', desc: 'Validate with real users and iterate based on findings.' },
  { step: 'Deliver', desc: 'Hand off production-ready assets and design documentation.' },
];

export default function UIUXDesign() {
  return (
    <PageLayout>
      <SEOHead
        title="UI/UX Design Services"
        description="User-centered UI/UX design for web and mobile applications. User research, wireframing, prototyping, visual design, and usability testing."
        keywords="UI design, UX design, user experience, user interface, wireframing, prototyping, usability testing, Figma, design system"
        url="/services/ui-ux-design"
      />
      {/* Hero */}
      <div style={{ background: '#F9FAFB', padding: '60px 24px 50px' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
            <span style={{ fontSize: '14px', color: '#6B7280' }}>Services</span>
            <span style={{ color: '#D1D5DB' }}>/</span>
            <span style={{ fontSize: '14px', color: '#0099CC', fontWeight: '500' }}>UI/UX Design</span>
          </div>
          <h1 style={{ fontSize: 'clamp(32px, 4vw, 52px)', fontWeight: '800', color: '#111827', marginBottom: '16px', letterSpacing: '-1px', lineHeight: 1.15 }}>
            UI/UX Design
          </h1>
          <p style={{ fontSize: '18px', color: '#6B7280', maxWidth: '640px', lineHeight: 1.7 }}>
            User-centered design that creates seamless, intuitive, and visually stunning digital experiences your users will love.
          </p>
        </div>
      </div>

      {/* Intro */}
      <section style={{ padding: '80px 24px', background: 'white' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '80px', alignItems: 'center' }}>
          <div>
            <h2 style={{ fontSize: '36px', fontWeight: '700', color: '#111827', marginBottom: '24px', letterSpacing: '-0.5px' }}>Design That Drives Results</h2>
            <p style={{ fontSize: '16px', color: '#6B7280', lineHeight: 1.8, marginBottom: '20px' }}>
              Great design is more than aesthetics — it\'s the difference between a product people love and one they abandon. Every design decision we make is grounded in user research, business goals, and proven interaction patterns.
            </p>
            <p style={{ fontSize: '16px', color: '#6B7280', lineHeight: 1.8 }}>
              From enterprise dashboards to consumer mobile apps, DISION Tech\'s design team blends strategy, research, and craft to create products that convert, retain, and delight.
            </p>
          </div>
          <div style={{ borderRadius: '20px', overflow: 'hidden', lineHeight: 0 }}>
            <img src="/pictures/intro_bg2_7e254d05c7.png" alt="UI UX Design" style={{ width: '100%', height: 'auto', display: 'block' }} onError={e => { e.target.src = 'https://images.unsplash.com/photo-1561070791-2526d30994b5?w=600&q=80'; }} />
          </div>
        </div>
      </section>

      {/* Services */}
      <section style={{ padding: '80px 24px', background: '#F9FAFB' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{ fontSize: '36px', fontWeight: '700', color: '#111827', marginBottom: '12px', letterSpacing: '-0.5px' }}>Our Design Services</h2>
          <p style={{ fontSize: '17px', color: '#6B7280', marginBottom: '56px', maxWidth: '500px' }}>Full-spectrum UX/UI capabilities from research to final delivery.</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '24px' }}>
            {designServices.map((s, i) => (
              <div key={i} className="service-card" style={{ background: 'white', borderRadius: '16px', padding: '28px', border: '1px solid #E5E7EB' }}>
                <div style={{ width: '52px', height: '52px', borderRadius: '12px', background: '#F0F9FF', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '18px', padding: '11px' }}>
                  <img src={s.icon} alt={s.title} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                </div>
                <h3 style={{ fontSize: '17px', fontWeight: '700', color: '#111827', marginBottom: '10px' }}>{s.title}</h3>
                <p style={{ fontSize: '14px', color: '#6B7280', lineHeight: 1.7, margin: 0 }}>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section style={{ padding: '80px 24px', background: 'white' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{ fontSize: '36px', fontWeight: '700', color: '#111827', marginBottom: '12px', letterSpacing: '-0.5px' }}>Our Design Process</h2>
          <p style={{ fontSize: '17px', color: '#6B7280', marginBottom: '56px', maxWidth: '480px' }}>A human-centered, iterative process that reduces risk and maximizes impact.</p>
          <div style={{ display: 'flex', gap: '0', position: 'relative' }}>
            <div style={{ position: 'absolute', top: '22px', left: '10%', right: '10%', height: '2px', background: '#E5E7EB', zIndex: 0 }} />
            {process.map((step, i) => (
              <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', padding: '0 12px', position: 'relative', zIndex: 1 }}>
                <div style={{ width: '44px', height: '44px', borderRadius: '50%', backgroundColor: '#0099CC', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px', flexShrink: 0 }}>
                  <span style={{ color: 'white', fontWeight: '700', fontSize: '13px' }}>0{i + 1}</span>
                </div>
                <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#111827', marginBottom: '8px' }}>{step.step}</h3>
                <p style={{ fontSize: '13px', color: '#6B7280', lineHeight: 1.6, margin: 0 }}>{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </PageLayout>
  );
}
